import { Link, useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { productImageFor } from "@/lib/stockImages";
import { titleCase } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Catalogue imports fill different fields: some have fabricType, others only a
// composition such as "100% Solution Dyed Polyester".
export function productMaterial(p) {
  if (p.fabricType) return p.fabricType;
  if (!p.composition) return null;
  return p.composition.replace(/\d+(\.\d+)?\s*%/g, "").replace(/\s+/g, " ").trim() || null;
}

// `design` is a plain string on some imports and a design record on others.
export function productDesign(p) {
  return typeof p.design === "string" ? p.design : p.design?.name || null;
}

export function productColour(p) {
  return p.colourFamily || p.colour || null;
}

export function productTypeLabel(p) {
  return p.type ? titleCase(p.type) : null;
}

export function productCatalogueLabel(p) {
  return [p.companyName, p.versionName].filter(Boolean).join(" – ");
}

export async function copyProductCode(p) {
  try {
    await navigator.clipboard.writeText(p.shopReference);
    toast.success(`Copied ${p.shopReference}`);
  } catch {
    toast.error("Couldn't copy the code");
  }
}

export function ProductMenu({ p }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`More actions for ${p.name}`}
          className="p-1 -mr-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary shrink-0"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate(`/products/${p.id}`)}>View details</DropdownMenuItem>
        {p.shopReference && <DropdownMenuItem onClick={() => copyProductCode(p)}>Copy product code</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ProductCard({ p, showCatalogue = true }) {
  const tags = [productTypeLabel(p), productDesign(p), productMaterial(p)].filter(Boolean).slice(0, 3);
  const colour = productColour(p);
  return (
    <div
      data-testid={`product-card-${p.id}`}
      className="group rounded-2xl overflow-hidden border border-border bg-card hover:shadow-lg transition-shadow"
    >
      <Link to={`/products/${p.id}`} data-testid={`product-card-link-${p.id}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
          <img
            src={p.imageUrls?.[0] || productImageFor(p.id)}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          {colour && (
            <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1.5 rounded-full bg-card/90 backdrop-blur px-2.5 py-1 text-[11px] text-foreground">
              {p.colourHex && <span className="h-2.5 w-2.5 rounded-full border border-black/10" style={{ backgroundColor: p.colourHex }} />}
              {colour}
            </span>
          )}
        </div>
      </Link>
      <div className="p-4">
        {showCatalogue && productCatalogueLabel(p) && (
          <p className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground truncate">
            {productCatalogueLabel(p)}
          </p>
        )}
        <div className="flex items-start justify-between gap-2 mt-1">
          <Link to={`/products/${p.id}`} className="font-heading text-lg leading-snug text-foreground hover:text-primary truncate">
            {p.name}
          </Link>
          <ProductMenu p={p} />
        </div>
        {p.shopReference && <p className="text-xs font-mono-spec text-muted-foreground mt-0.5">{p.shopReference}</p>}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] text-foreground/80 max-w-full truncate">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ProductRow({ p, showCatalogue = true }) {
  return (
    <li data-testid={`product-row-${p.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-secondary/40 transition-colors">
      <Link to={`/products/${p.id}`} className="shrink-0">
        <img src={p.imageUrls?.[0] || productImageFor(p.id)} alt="" loading="lazy" className="h-14 w-20 rounded-lg object-cover" />
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/products/${p.id}`} className="font-medium text-foreground hover:text-primary">{p.name}</Link>
        <p className="text-xs text-muted-foreground truncate">
          <span className="font-mono-spec">{p.shopReference}</span>
          {showCatalogue && productCatalogueLabel(p) && ` · ${productCatalogueLabel(p)}`}
        </p>
      </div>
      <span className="hidden md:block w-28 text-sm text-foreground/80 truncate">{productTypeLabel(p) || "—"}</span>
      <span className="hidden lg:block w-48 text-sm text-foreground/80 truncate">{productMaterial(p) || "—"}</span>
      <span className="hidden md:flex w-28 items-center gap-1.5 text-sm text-foreground/80 truncate">
        {p.colourHex && <span className="h-3 w-3 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: p.colourHex }} />}
        {productColour(p) || "—"}
      </span>
      <ProductMenu p={p} />
    </li>
  );
}
